import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search,
  X,
  TrendingUp,
  Calculator,
  ArrowRightLeft,
  Landmark,
  Sparkles,
  Receipt,
  ArrowRight,
  Clock,
  Zap,
  Globe,
  Coins,
  BookOpen,
  HelpCircle,
  Code,
  SlidersHorizontal,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
  Tag,
  Percent,
  DollarSign,
  ArrowUpRight,
  CornerDownLeft,
  Layers,
  Sparkle
} from 'lucide-react';
import { AppView, RegionInfo, ToolItem } from '../../types';
import {
  publicSearchEngine,
  SearchCategoryGroup,
  SearchResultItem
} from '../../services/search/searchEngine';

interface SmartSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTool: (toolId: string) => void;
  onNavigateView?: (view: AppView) => void;
  onSelectRegion?: (region: RegionInfo) => void;
  tools?: ToolItem[];
  selectedRegion?: RegionInfo;
}

const CATEGORY_GROUPS: (SearchCategoryGroup | 'ALL')[] = [
  'ALL',
  'Tools',
  'Currencies',
  'Guides',
  'Countries / Markets',
  'Help',
  'Developer Documentation'
];

export const SmartSearchModal: React.FC<SmartSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectTool,
  onNavigateView,
  onSelectRegion,
  selectedRegion
}) => {
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<SearchCategoryGroup | 'ALL'>('ALL');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [aiSemanticActive, setAiSemanticActive] = useState<boolean>(false);
  const [aiThinking, setAiThinking] = useState<boolean>(false);

  // Recent searches saved locally
  const [recentQueries, setRecentQueries] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('yamoopay_recent_search_queries');
      return stored
        ? JSON.parse(stored)
        : [
            'USD to EUR',
            '$85,000 salary in UK',
            'Mortgage $400k at 6.2%',
            '15% tip on $120',
            'Global VAT rates'
          ];
    } catch {
      return ['USD to EUR', '$85,000 salary in UK', 'Mortgage $400k at 6.2%'];
    }
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Focus on Open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
      setSelectedIndex(0);
    } else {
      setQuery('');
      setActiveGroup('ALL');
      setAiSemanticActive(false);
    }
  }, [isOpen]);

  // Execute Search via Deterministic Engine
  const searchResults = useMemo(() => {
    const regionCode = selectedRegion?.code || 'US';
    return publicSearchEngine.search(query, regionCode, activeGroup);
  }, [query, selectedRegion, activeGroup]);

  const flatList = searchResults.flatRankedResults;

  // Reset keyboard selected index when query or tab changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, activeGroup]);

  // Scroll active item into view
  useEffect(() => {
    if (resultsContainerRef.current) {
      const activeEl = resultsContainerRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  // Save Recent Searches
  const recordSearchQuery = (str: string) => {
    const trimmed = str.trim();
    if (!trimmed) return;
    const updated = [trimmed, ...recentQueries.filter((q) => q !== trimmed)].slice(0, 6);
    setRecentQueries(updated);
    try {
      localStorage.setItem('yamoopay_recent_search_queries', JSON.stringify(updated));
    } catch (e) {
      console.error(e);
    }
  };

  // Action Dispatcher
  const handleItemSelect = (item: SearchResultItem) => {
    recordSearchQuery(query || item.title);

    switch (item.action.type) {
      case 'tool':
        onSelectTool(item.action.targetId);
        onClose();
        break;
      case 'currency':
        onSelectTool('currency-converter');
        onClose();
        break;
      case 'region':
        if (onSelectRegion && item.action.payload) {
          onSelectRegion(item.action.payload);
        }
        if (onNavigateView) {
          onNavigateView('homepage');
        }
        onClose();
        break;
      case 'guide':
        if (onNavigateView) {
          onNavigateView('financial-guides');
        }
        onClose();
        break;
      case 'api':
        if (onNavigateView) {
          onNavigateView('developer-portal');
        }
        onClose();
        break;
      case 'view':
        if (onNavigateView) {
          onNavigateView(item.action.targetId as AppView);
        }
        onClose();
        break;
      default:
        onSelectTool('currency-converter');
        onClose();
    }
  };

  // Keyboard Navigation: Arrow Up, Arrow Down, Enter, Escape, Tab
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flatList.length > 0) {
        setSelectedIndex((prev) => (prev + 1) % flatList.length);
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flatList.length > 0) {
        setSelectedIndex((prev) => (prev - 1 + flatList.length) % flatList.length);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchResults.directAnswer) {
        onSelectTool(searchResults.directAnswer.actionToolId);
        recordSearchQuery(query);
        onClose();
        return;
      }
      if (flatList.length > 0 && flatList[selectedIndex]) {
        handleItemSelect(flatList[selectedIndex]);
      }
      return;
    }

    // Tab key cycling through Category Groups
    if (e.key === 'Tab') {
      e.preventDefault();
      const currentIdx = CATEGORY_GROUPS.indexOf(activeGroup);
      const nextIdx = e.shiftKey
        ? (currentIdx - 1 + CATEGORY_GROUPS.length) % CATEGORY_GROUPS.length
        : (currentIdx + 1) % CATEGORY_GROUPS.length;
      setActiveGroup(CATEGORY_GROUPS[nextIdx]);
    }
  };

  // Trigger optional AI semantic enhancement
  const handleTriggerAiSemantic = () => {
    setAiThinking(true);
    setTimeout(() => {
      setAiThinking(false);
      setAiSemanticActive(true);
    }, 450);
  };

  // Dynamic Icon Renderer
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'Percent':
        return <Percent className="w-4 h-4 text-emerald-600" />;
      case 'Tag':
        return <Tag className="w-4 h-4 text-indigo-600" />;
      case 'Coins':
        return <Coins className="w-4 h-4 text-amber-600" />;
      case 'BookOpen':
        return <BookOpen className="w-4 h-4 text-blue-600" />;
      case 'Globe':
        return <Globe className="w-4 h-4 text-teal-600" />;
      case 'HelpCircle':
        return <HelpCircle className="w-4 h-4 text-purple-600" />;
      case 'Code':
        return <Code className="w-4 h-4 text-slate-700" />;
      case 'ArrowRightLeft':
        return <ArrowRightLeft className="w-4 h-4 text-cyan-600" />;
      case 'Landmark':
        return <Landmark className="w-4 h-4 text-emerald-700" />;
      case 'Receipt':
        return <Receipt className="w-4 h-4 text-orange-600" />;
      default:
        return <Calculator className="w-4 h-4 text-slate-600" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="global-search-title"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 md:p-6 bg-slate-950/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-150"
      onClick={onClose}
    >
      {/* Search Sheet: Full screen on mobile, modal on desktop */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full h-full sm:h-auto sm:max-h-[85vh] sm:max-w-3xl bg-white sm:rounded-2xl shadow-2xl border-0 sm:border border-[#E2E8F0] flex flex-col overflow-hidden text-[#1E293B] font-sans antialiased animate-in zoom-in-98 duration-150"
      >
        {/* ============================================================= */}
        {/* 1. TOP SEARCH INPUT BAR                                       */}
        {/* ============================================================= */}
        <div className="p-4 sm:p-5 border-b border-[#E2E8F0] bg-white flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-[#0F172A] shrink-0 shadow-2xs">
            <Search className="w-4 h-4 text-[#0F172A]" />
          </div>

          <div className="flex-1 flex flex-col justify-center">
            <input
              id="global-search-title"
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What do you want to calculate?"
              className="w-full text-base sm:text-lg font-bold text-[#0F172A] placeholder-[#94A3B8] bg-transparent outline-none tracking-tight"
            />
          </div>

          {/* Clear Query Button */}
          {query && (
            <button
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Clear input"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* AI Semantic Enhancement Trigger (Optional per spec) */}
          <button
            onClick={handleTriggerAiSemantic}
            disabled={aiThinking}
            title="Optional AI semantic understanding enhancement"
            className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all cursor-pointer ${
              aiSemanticActive
                ? 'bg-[#EEF2FF] border-[#C7D2FE] text-[#4338CA]'
                : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
            }`}
          >
            <Sparkles className={`w-3.5 h-3.5 ${aiThinking ? 'animate-spin text-indigo-600' : ''}`} />
            <span>{aiThinking ? 'Thinking...' : aiSemanticActive ? 'AI Enhanced' : 'AI Mode'}</span>
          </button>

          {/* Close Escape Badge */}
          <button
            onClick={onClose}
            className="sm:hidden p-2 text-[#64748B] hover:text-[#0F172A] rounded-lg cursor-pointer"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <kbd className="hidden sm:inline-block px-2 py-1 text-[10px] font-mono font-bold bg-[#F1F5F9] text-[#64748B] rounded-md border border-[#CBD5E1]">
            ESC
          </kbd>
        </div>

        {/* ============================================================= */}
        {/* 2. CATEGORY GROUPS FILTER BAR                                 */}
        {/* ============================================================= */}
        <div className="px-4 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0] flex items-center gap-1.5 overflow-x-auto no-scrollbar shrink-0">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mr-1 hidden md:inline">
            Groups:
          </span>
          {CATEGORY_GROUPS.map((group) => {
            const isActive = activeGroup === group;
            const count =
              group === 'ALL'
                ? searchResults.flatRankedResults.length
                : searchResults.resultsByGroup[group as SearchCategoryGroup]?.length || 0;

            return (
              <button
                key={group}
                onClick={() => setActiveGroup(group)}
                className={`px-3 py-1 rounded-lg text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                  isActive
                    ? 'bg-[#0F172A] text-white shadow-2xs'
                    : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#E2E8F0]/60'
                }`}
              >
                <span>{group}</span>
                {count > 0 && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-semibold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-[#E2E8F0] text-[#475569]'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ============================================================= */}
        {/* 3. SCROLLABLE SEARCH RESULTS & INTELLIGENT CONTENT            */}
        {/* ============================================================= */}
        <div
          ref={resultsContainerRef}
          className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5 divide-y divide-[#F1F5F9]"
        >
          {/* A. DIRECT INSTANT CALCULATION ANSWER (if detected) */}
          {searchResults.directAnswer && (
            <div className="pb-4">
              <div
                onClick={() => {
                  onSelectTool(searchResults.directAnswer!.actionToolId);
                  recordSearchQuery(query);
                  onClose();
                }}
                className="p-4 bg-gradient-to-r from-[#EFF6FF] to-[#F0FDF4] border border-[#BFDBFE] rounded-2xl shadow-2xs flex items-center justify-between gap-4 hover:border-[#93C5FD] transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white border border-[#BFDBFE] text-[#1E40AF] flex items-center justify-center shrink-0 shadow-2xs">
                    <Zap className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-[#1E40AF] uppercase tracking-wider">
                        {searchResults.directAnswer.title}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DCFCE7] text-[#15803D]">
                        Instant Result
                      </span>
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-[#0F172A] tracking-tight mt-0.5">
                      {searchResults.directAnswer.value}
                    </div>
                    <div className="text-xs text-[#64748B] mt-0.5">
                      {searchResults.directAnswer.detail}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#CBD5E1] rounded-xl text-xs font-bold text-[#0F172A] group-hover:bg-[#0F172A] group-hover:text-white transition-all shrink-0">
                  <span>Open Calculator</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          )}

          {/* B. RECENT SEARCHES & POPULAR INTENT STARTERS (When query is empty) */}
          {!query && (
            <div className="space-y-4 pt-1">
              {/* Recent Searches */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Recent Calculations & Searches</span>
                  </div>
                  <button
                    onClick={() => {
                      setRecentQueries([]);
                      localStorage.removeItem('yamoopay_recent_search_queries');
                    }}
                    className="text-[10px] text-[#94A3B8] hover:text-[#0F172A] normal-case font-semibold cursor-pointer"
                  >
                    Clear history
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {recentQueries.map((rq) => (
                    <button
                      key={rq}
                      onClick={() => setQuery(rq)}
                      className="px-3 py-1.5 bg-[#F8FAFC] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl text-xs font-medium text-[#334155] hover:text-[#0F172A] flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <Search className="w-3 h-3 text-[#94A3B8]" />
                      <span>{rq}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Calculation Shortcuts */}
              <div className="space-y-2 pt-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span>Popular Calculations by Group</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      onSelectTool('currency-converter');
                      onClose();
                    }}
                    className="p-3 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] hover:border-[#CBD5E1] rounded-xl text-left flex items-center justify-between transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-xs">
                        FX
                      </div>
                      <div>
                        <div className="text-xs font-bold text-[#0F172A]">
                          Currency & FX Converter
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                          Mid-market rates for 170+ fiat & crypto
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#0F172A] transition-colors" />
                  </button>

                  <button
                    onClick={() => {
                      onSelectTool('salary-calculator');
                      onClose();
                    }}
                    className="p-3 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] hover:border-[#CBD5E1] rounded-xl text-left flex items-center justify-between transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center font-bold text-xs">
                        TAX
                      </div>
                      <div>
                        <div className="text-xs font-bold text-[#0F172A]">
                          Salary & Paycheck Calculator
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                          Gross to net in US, UK, France, Germany, Canada
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#0F172A] transition-colors" />
                  </button>

                  <button
                    onClick={() => {
                      onSelectTool('mortgage-calculator');
                      onClose();
                    }}
                    className="p-3 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] hover:border-[#CBD5E1] rounded-xl text-left flex items-center justify-between transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold text-xs">
                        MORT
                      </div>
                      <div>
                        <div className="text-xs font-bold text-[#0F172A]">
                          Mortgage & Housing Loan
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                          Monthly P&I payment and amortization schedule
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#0F172A] transition-colors" />
                  </button>

                  <button
                    onClick={() => {
                      onSelectTool('compare-transfers');
                      onClose();
                    }}
                    className="p-3 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] hover:border-[#CBD5E1] rounded-xl text-left flex items-center justify-between transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center font-bold text-xs">
                        SEND
                      </div>
                      <div>
                        <div className="text-xs font-bold text-[#0F172A]">
                          Compare Money Transfers
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                          Wise, Western Union, Remitly, Moneroo fees
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#0F172A] transition-colors" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* C. DETERMINISTIC RANKED SEARCH RESULTS */}
          <div className="space-y-2 pt-2">
            {/* Results Header with Deterministic Ranking Order Badge */}
            {query && (
              <div className="flex items-center justify-between pb-1">
                <span className="text-[11px] font-bold text-[#64748B]">
                  {flatList.length} matching {flatList.length === 1 ? 'item' : 'items'}
                </span>
                <div className="flex items-center gap-2 text-[10px] text-[#94A3B8] font-mono">
                  <span>Ranked by: exact › alias › category › popularity › market</span>
                </div>
              </div>
            )}

            {/* Zero Results State */}
            {flatList.length === 0 && query && (
              <div className="py-12 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center mx-auto text-[#94A3B8]">
                  <Search className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#0F172A]">
                    No direct results for &ldquo;{query}&rdquo;
                  </h4>
                  <p className="text-xs text-[#64748B] mt-1 max-w-sm mx-auto">
                    Try searching for a currency (e.g. <em>USD</em>, <em>EUR</em>), a calculation (
                    <em>$75,000 salary</em>), or a jurisdiction (<em>United Kingdom</em>).
                  </p>
                </div>
                <div className="pt-2">
                  <button
                    onClick={() => setQuery('')}
                    className="px-4 py-2 bg-[#0F172A] text-white rounded-xl text-xs font-bold hover:bg-[#1E293B] transition-colors cursor-pointer"
                  >
                    View All Popular Tools
                  </button>
                </div>
              </div>
            )}

            {/* Results List */}
            {flatList.map((item, index) => {
              const isSelected = index === selectedIndex;

              return (
                <div
                  key={item.id}
                  data-index={index}
                  onClick={() => handleItemSelect(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`p-3 sm:p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                    isSelected
                      ? 'bg-[#F1F5F9] border-[#0F172A] shadow-xs'
                      : 'bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]'
                  }`}
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    {/* Icon */}
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border transition-all ${
                        isSelected
                          ? 'bg-white border-[#CBD5E1] shadow-2xs'
                          : 'bg-[#F8FAFC] border-[#E2E8F0]'
                      }`}
                    >
                      {renderIcon(item.iconName)}
                    </div>

                    {/* Titles */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs sm:text-sm font-bold text-[#0F172A] truncate">
                          {item.title}
                        </span>

                        {/* Group Pill */}
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#E2E8F0] text-[#475569] uppercase tracking-wider">
                          {item.group}
                        </span>

                        {/* Badge */}
                        {item.badgeText && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              item.badgeType === 'primary'
                                ? 'bg-[#EFF6FF] text-[#2563EB]'
                                : item.badgeType === 'success'
                                ? 'bg-[#DCFCE7] text-[#15803D]'
                                : item.badgeType === 'warning'
                                ? 'bg-[#FEF3C7] text-[#B45309]'
                                : item.badgeType === 'info'
                                ? 'bg-[#E0E7FF] text-[#4338CA]'
                                : 'bg-[#F1F5F9] text-[#64748B]'
                            }`}
                          >
                            {item.badgeText}
                          </span>
                        )}

                        {/* Match Reason Tag */}
                        {query && (
                          <span className="text-[10px] text-[#94A3B8] font-mono hidden md:inline">
                            [{item.matchReason}]
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-[#64748B] truncate mt-0.5">
                        {item.subtitle}
                      </div>
                    </div>
                  </div>

                  {/* Right Action Hint */}
                  <div className="flex items-center gap-2 shrink-0">
                    {isSelected && (
                      <span className="hidden sm:flex items-center gap-1 text-[11px] font-bold text-[#0F172A]">
                        <span>Select</span>
                        <CornerDownLeft className="w-3.5 h-3.5" />
                      </span>
                    )}
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${
                        isSelected ? 'text-[#0F172A] translate-x-0.5' : 'text-[#CBD5E1]'
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ============================================================= */}
        {/* 4. KEYBOARD SHORTCUTS & GOVERNED FOOTER                       */}
        {/* ============================================================= */}
        <div className="p-3 sm:px-5 sm:py-3 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center justify-between text-xs text-[#64748B] shrink-0">
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-white rounded border border-[#CBD5E1] text-[#0F172A]">
                ↑
              </kbd>
              <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-white rounded border border-[#CBD5E1] text-[#0F172A]">
                ↓
              </kbd>
              <span className="text-[11px]">Navigate</span>
            </div>

            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-white rounded border border-[#CBD5E1] text-[#0F172A]">
                ↵
              </kbd>
              <span className="text-[11px]">Open calculation</span>
            </div>

            <div className="flex items-center gap-1 hidden md:flex">
              <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-white rounded border border-[#CBD5E1] text-[#0F172A]">
                Tab
              </kbd>
              <span className="text-[11px]">Switch group</span>
            </div>

            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-white rounded border border-[#CBD5E1] text-[#0F172A]">
                Esc
              </kbd>
              <span className="text-[11px]">Close</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[11px] font-semibold text-[#0F172A]">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0E7044]" />
            <span className="hidden sm:inline">YamooPay Certified Calculations</span>
          </div>
        </div>
      </div>
    </div>
  );
};
